import React from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useTheme } from "@/contexts/ThemeContext";
import {
  ColorThemes,
  ColorTheme,
  AccessibilityMode,
  TextSize,
  AppearanceMode,
} from "@/constants/themes";
import { Sun, Moon, Monitor, Eye, Accessibility, Contrast, Type } from "lucide-react";

export function Appearance() {
  const {
    themeConfig,
    updateColorTheme,
    updateAccessibilityMode,
    updateTextSize,
    updateAppearanceMode,
    resetToDefault,
  } = useTheme();

  const appearanceModes = [
    {
      value: "light" as AppearanceMode,
      label: "Light Mode",
      icon: Sun,
      description: "Use light appearance",
    },
    {
      value: "dark" as AppearanceMode,
      label: "Dark Mode",
      icon: Moon,
      description: "Use dark appearance",
    },
    {
      value: "system" as AppearanceMode,
      label: "System",
      icon: Monitor,
      description: "Follow device settings",
    },
  ];

  const accessibilityModes = [
    {
      value: "normal" as AccessibilityMode,
      label: "Normal",
      icon: Eye,
      description: "Standard appearance with default colors",
    },
    {
      value: "colorBlind" as AccessibilityMode,
      label: "Color Blind Friendly",
      icon: Accessibility,
      description: "High contrast colors for color vision deficiency",
    },
    {
      value: "highContrast" as AccessibilityMode,
      label: "High Contrast",
      icon: Contrast,
      description: "Maximum contrast for better visibility",
    },
  ];

  const textSizes = [
    {
      value: "small" as TextSize,
      label: "Small",
      description: "Compact text size for more content",
    },
    {
      value: "medium" as TextSize,
      label: "Medium",
      description: "Standard text size (default)",
    },
    {
      value: "large" as TextSize,
      label: "Large",
      description: "Larger text for better readability",
    },
    {
      value: "extraLarge" as TextSize,
      label: "Extra Large",
      description: "Maximum text size for accessibility",
    },
  ];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Appearance</h1>
        <p className="text-muted-foreground">
          Customize the look and feel of your admin panel
        </p>
      </div>

      {/* Color Theme Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <div
              className="w-5 h-5 rounded-full"
              style={{ backgroundColor: ColorThemes[themeConfig.colorTheme].primary }}
            />
            Color Theme
          </CardTitle>
          <CardDescription>Choose your preferred color scheme</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {Object.entries(ColorThemes).map(([key, theme]) => (
              <button
                key={key}
                onClick={() => {
                  updateColorTheme(key as ColorTheme);
                }}
                className={`p-4 rounded-lg border-2 transition-all hover:scale-105 ${
                  themeConfig.colorTheme === key
                    ? "border-primary ring-2 ring-primary/20"
                    : "border-border hover:border-primary/50"
                }`}
              >
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-4 h-4 rounded-full"
                        style={{ backgroundColor: theme.primary }}
                      />
                      <span className="font-medium">{theme.name}</span>
                    </div>
                    {themeConfig.colorTheme === key && (
                      <div className="w-2 h-2 rounded-full bg-primary" />
                    )}
                  </div>
                  <div className="flex gap-1">
                    <div
                      className="w-6 h-3 rounded"
                      style={{ backgroundColor: theme.primary }}
                    />
                    <div
                      className="w-6 h-3 rounded"
                      style={{ backgroundColor: theme.secondary }}
                    />
                    <div
                      className="w-6 h-3 rounded"
                      style={{ backgroundColor: theme.accent }}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground text-left">
                    {theme.description}
                  </p>
                </div>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Appearance Mode Section */}
      <Card>
        <CardHeader>
          <CardTitle>Appearance Mode</CardTitle>
          <CardDescription>Choose how the app should appear</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4">
            {appearanceModes.map((mode) => (
              <button
                key={mode.value}
                onClick={() => {
                  updateAppearanceMode(mode.value);
                }}
                className={`flex items-center justify-between p-4 rounded-lg border-2 transition-all hover:scale-105 ${
                  themeConfig.appearanceMode === mode.value
                    ? "border-primary ring-2 ring-primary/20"
                    : "border-border hover:border-primary/50"
                }`}
              >
                <div className="flex items-center gap-3">
                  <mode.icon className="w-5 h-5" />
                  <div className="text-left">
                    <div className="font-medium">{mode.label}</div>
                    <div className="text-sm text-muted-foreground">
                      {mode.description}
                    </div>
                  </div>
                </div>
                {themeConfig.appearanceMode === mode.value && (
                  <div className="w-2 h-2 rounded-full bg-primary" />
                )}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Accessibility Section */}
      <Card>
        <CardHeader>
          <CardTitle>Accessibility</CardTitle>
          <CardDescription>Optimize the app for better accessibility</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4">
            {accessibilityModes.map((mode) => (
              <button
                key={mode.value}
                onClick={() => {
                  updateAccessibilityMode(mode.value);
                }}
                className={`flex items-center justify-between p-4 rounded-lg border-2 transition-all hover:scale-105 ${
                  themeConfig.accessibilityMode === mode.value
                    ? "border-primary ring-2 ring-primary/20"
                    : "border-border hover:border-primary/50"
                }`}
              >
                <div className="flex items-center gap-3">
                  <mode.icon className="w-5 h-5" />
                  <div className="text-left">
                    <div className="font-medium">{mode.label}</div>
                    <div className="text-sm text-muted-foreground">
                      {mode.description}
                    </div>
                  </div>
                </div>
                {themeConfig.accessibilityMode === mode.value && (
                  <div className="w-2 h-2 rounded-full bg-primary" />
                )}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Text Size Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Type className="w-5 h-5" />
            Text Size
          </CardTitle>
          <CardDescription>Adjust text size for better readability</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4">
            {textSizes.map((size) => (
              <button
                key={size.value}
                onClick={() => {
                  updateTextSize(size.value);
                }}
                className={`flex items-center justify-between p-4 rounded-lg border-2 transition-all hover:scale-105 ${
                  themeConfig.textSize === size.value
                    ? "border-primary ring-2 ring-primary/20"
                    : "border-border hover:border-primary/50"
                }`}
              >
                <div className="text-left">
                  <div className="font-medium">{size.label}</div>
                  <div className="text-sm text-muted-foreground">{size.description}</div>
                </div>
                {themeConfig.textSize === size.value && (
                  <div className="w-2 h-2 rounded-full bg-primary" />
                )}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Reset Button */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium">Reset to Default</h3>
              <p className="text-sm text-muted-foreground">
                Reset all appearance settings to their default values
              </p>
            </div>
            <Button variant="outline" onClick={resetToDefault}>
              Reset
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
